import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import Chart from "react-apexcharts";
import { useGetCapacityDashboardQuery } from "../store/api/dashboardApi";

const CHART_COLORS = {
  total: "#c7d2fe",
  available: "#818cf8",
  allocated: "#4f46e5",
  deployed: "#3730a3",
};

function CapacityDashboard(props) {
  return (
    <div className="">
      {/* <div className="-m-2 mb-6 px-6 py-2 bg-rose-100 border border-rose-500 text-rose-500 rounded-md flex items-center space-x-2">
                <TriangleAlert className="w-5" />
                <div>Temporary placeholder for the upcoming dashboard</div>
            </div> */}
      <h1 className="text-md font-bold mb-4">Capacity Dashboard</h1>
      <Tabs defaultValue="compute">
        <TabsList className="mb-4">
          <TabsTrigger className="min-w-40" value="compute">
            Compute
          </TabsTrigger>
          <TabsTrigger className="min-w-40" value="alml-services">
            AI/ML Services
          </TabsTrigger>
        </TabsList>
        <TabsContent value="compute">
          <ComputeCapacityTab />
        </TabsContent>
        <TabsContent value="alml-services"></TabsContent>
      </Tabs>
    </div>
  );
}

function ComputeCapacityTab() {
  const { data, isLoading } = useGetCapacityDashboardQuery();

  if (isLoading) return "Loading...";

  return (
    <div className="">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4">
          <OverallComputeCapacity data={data.overall} />
        </div>
        <div className="col-span-8">
          <OverallComputeCapacityBarChart data={data.computes} />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-12 gap-4">
        {data?.computes.map((capacity, index) => (
          <div key={index} className="col-span-3">
            <CapacityUnits
              title={`${
                capacity.label?.trim().length
                  ? capacity.label
                  : capacity.computeId
              }`}
              data={[
                { label: "Total", value: capacity.total, class: "bg-gray-100" },
                {
                  label: "Available",
                  value: capacity.available,
                  class: "bg-gray-200",
                },
                {
                  label: "Allocated",
                  value: capacity.allocated,
                  class: "bg-gray-400 text-white",
                },
                {
                  label: "Deployed",
                  value: capacity.deployed,
                  class: "bg-gray-500 text-white",
                },
              ]}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function OverallComputeCapacityBarChart({ data }) {
  const chartData = {
    options: {
      chart: {
        id: "compute-capacity-bar",
        stacked: true,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          columnWidth: "40%",
          horizontal: false,
          borderRadius: 0,
          borderRadiusApplication: "end",
        },
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        borderColor: "#e5e7eb", // <-- this changes ALL horizontal grid lines
        strokeDashArray: 0,
      },
      xaxis: {
        categories: data.map((i) =>
          i.label?.trim().length ? i.label : i.computeId
        ),
        labels: {
          show: true,
          rotate: -90,
          style: {
            fontSize: "10px",
            fontWeight: 600,
          },
        },
        lines: {
          show: true,
          color: "red",
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: { show: false },
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
      },
      legend: {
        show: true,
        markers: {
          radius: 2,
        },
      },
      states: {
        hover: {
          filter: {
            type: "none",
          },
        },
        active: {
          filter: {
            type: "none",
          },
        },
      },
    },
    series: [
      // {
      //     name: "Total",
      //     color: CHART_COLORS.total,
      //     data: data.map(c => c.total)
      // },
      {
        name: "Deployed",
        color: CHART_COLORS.deployed,
        data: data.map((c) => c.deployed),
      },
      {
        name: "Allocated",
        color: CHART_COLORS.allocated,
        data: data.map((c) => c.allocated),
      },
      {
        name: "Available",
        color: CHART_COLORS.available,
        data: data.map((c) => c.available),
      },
    ],
  };

  return (
    <Card className="py-4 gap-0">
      <CardHeader className="px-4">
        <CardTitle>AI Compute Capacity</CardTitle>
        <CardDescription>All Instances</CardDescription>
      </CardHeader>
      <CardContent className="px-2">
        <div>
          <Chart
            options={chartData.options}
            series={chartData.series}
            type="bar"
            className="w-full"
            height={290}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function OverallComputeCapacity({ data }) {
  const chartData = [
    // { label: "Total", value: data.total, fill: CHART_COLORS.total },
    { label: "Available", value: data.available, fill: CHART_COLORS.available },
    { label: "Allocated", value: data.allocated, fill: CHART_COLORS.allocated },
    { label: "Deployed", value: data.deployed, fill: CHART_COLORS.deployed },
  ];

  const chartConfig = {
    options: {
      chart: {
        type: "donut",
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
      },
      states: {
        hover: {
          filter: {
            type: "none",
          },
        },
        active: {
          filter: {
            type: "none",
          },
        },
      },
      labels: chartData.map((i) => i.label),
      colors: chartData.map((i) => i.fill),
      dataLabels: {
        dropShadow: {
          enabled: true,
          color: "#000000",
          opacity: 0.2,
          blur: 0.25,
        },
        style: {
          fontSize: "12px",
          fontWeight: 500,
          colors: ["#fff"],
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "70%",
            labels: {
              show: true,
              name: {
                show: true,
                offsetY: -6,
              },
              value: {
                show: true,
                fontSize: "20px",
                fontWeight: 600,
                color: "#111",
                offsetY: 2,
              },
              total: {
                show: true,
                label: "Total Units",
                showAlways: true,
                fontSize: "12px",
                fontWeight: 600,
                color: "#111",
                formatter: () => data.total,
              },
            },
          },
        },
      },
    },
    series: chartData.map((i) => Number(i.value)),
  };

  return (
    <Card className="py-4 gap-0 flex flex-col">
      <CardHeader className="px-4 items-center pb-0">
        <CardTitle>AI Compute Capacity</CardTitle>
        <CardDescription>Overall</CardDescription>
      </CardHeader>
      <CardContent className="px-2 pb-0">
        <div className="h-full w-full">
          <Chart
            options={chartConfig.options}
            series={chartConfig.series}
            type="donut"
            className="w-full"
            height={305}
            width="100%"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function CapacityUnits({ title, description = "", data }) {
  return (
    <Card className="py-4 gap-2 flex flex-col">
      <CardHeader className="px-4 items-center pb-0">
        <CardTitle className="text-sm truncate">
          {title}
          {/* <Tooltip>
                        <TooltipTrigger asChild>
                            <span>{title}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            {title}
                        </TooltipContent>
                    </Tooltip> */}
        </CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="px-4 flex-1 pb-0">
        <div className="grid grid-cols-2 rounded-md gap-1">
          {data.map((item, index) => (
            <div
              key={index}
              className={`aspect-square col-span-1 flex flex-col items-center justify-center rounded-md bg-gray-100 ${item.class}`}
            >
              <div className="font-medium text-xl">{item.value}</div>
              <div className="text-xs opacity-80">{item.label}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default CapacityDashboard;
